import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import LavorisService from '../../services/LavorisService';
import {
  ClientTicket,
  ClientTicketMessage,
} from '../../services/Crm/CrmTicketsServices';
import { RootState } from '../../store/combineReducer';

function formatDate(value?: string) {
  if (!value) return '';
  try {
    const trimmed = value.trim();
    // Fechas sin offset del API se interpretan como UTC
    const date =
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(trimmed) &&
      !/[zZ]|[+-]\d{2}:?\d{2}$/.test(trimmed)
        ? new Date(`${trimmed}Z`)
        : /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(trimmed)
          ? new Date(`${trimmed.replace(' ', 'T')}Z`)
          : new Date(trimmed);

    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleString('es-AR', {
      timeZone: 'America/Cordoba',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

export default function CrmTicketsPage() {
  const currentCompany = useSelector(
    (state: RootState) =>
      state.user.currentCompany || state.user.companies?.[0],
  );
  const companyId = currentCompany?.id as string | undefined;

  const [tickets, setTickets] = useState<ClientTicket[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list =
          await LavorisService.getInstance().CrmTickets.listTickets(companyId);
        if (!cancelled) {
          setTickets(list);
          setSelectedId(list[0]?.id || null);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setTickets([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const selected = useMemo(
    () => tickets.find((t) => t.id === selectedId) || null,
    [tickets, selectedId],
  );

  if (loading) {
    return <div className="p-6 text-gray-500">Cargando comunicaciones...</div>;
  }

  return (
    <div className="p-6 h-full flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Comunicaciones</h1>
        <p className="text-sm text-gray-500">
          Mensajes y documentos compartidos por el equipo de Lavoris
        </p>
      </div>

      {tickets.length === 0 ? (
        <p className="text-sm text-gray-400">
          No hay comunicaciones visibles por el momento.
        </p>
      ) : (
        <div className="flex flex-col md:flex-row gap-4 flex-1 min-h-0">
          <div className="md:w-80 space-y-2 overflow-y-auto">
            {tickets.map((ticket) => (
              <button
                key={ticket.id}
                type="button"
                onClick={() => setSelectedId(ticket.id)}
                className={`w-full text-left rounded-xl border px-4 py-3 ${
                  selectedId === ticket.id
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <p className="font-medium text-sm">{ticket.title}</p>
                {ticket.stageName && (
                  <p className="text-xs text-gray-500 mt-1">{ticket.stageName}</p>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  {formatDate(ticket.lastActionAt)}
                </p>
              </button>
            ))}
          </div>

          <div className="flex-1 bg-white rounded-xl border overflow-hidden flex flex-col min-h-[420px]">
            {selected ? (
              <>
                <div className="border-b px-4 py-3">
                  <h2 className="font-semibold">{selected.title}</h2>
                  {selected.stageName && (
                    <p className="text-xs text-gray-500">{selected.stageName}</p>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
                  {(selected.messages || []).map((msg: ClientTicketMessage) => (
                    <div
                      key={msg.id}
                      className="max-w-[90%] rounded-2xl rounded-bl-md bg-white border px-3 py-2 shadow-sm"
                    >
                      <p className="text-[10px] text-gray-400 mb-1">
                        {msg.createdBy?.name || 'Lavoris'}
                        {' · '}
                        {formatDate(msg.actionDate || msg.createdDate)}
                      </p>
                      <p className="text-sm whitespace-pre-wrap">
                        {msg.description}
                      </p>
                      {!!msg.attachments?.length && (
                        <ul className="mt-2 space-y-1">
                          {msg.attachments.map((att) => (
                            <li key={att.id} className="text-xs text-blue-600">
                              📎 {att.fileName}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="p-6 text-sm text-gray-400">
                Seleccioná una comunicación
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
